/**
 * Subjects Service
 *
 * Manages the school subjects catalog - standardized subjects with teacher linking.
 * Subjects replace the free-text subject field on classes with a proper collection.
 *
 * Collection: schools/{schoolId}/subjects
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Cache
let cachedSubjects = null;
let cacheSchoolId = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Default color palette for subjects
const DEFAULT_COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
  '#14B8A6', // teal
  '#6366F1', // indigo
];

class SubjectsService {
  constructor() {
    this.collectionName = 'subjects';
    this.listeners = new Map();
  }

  /**
   * Get collection reference for a specific school
   */
  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('School ID is required for subjects operations');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Clear cache
   */
  clearCache() {
    cachedSubjects = null;
    cacheSchoolId = null;
    cacheTimestamp = null;
  }

  /**
   * Get all subjects for a school (with caching)
   */
  async getAll(schoolId) {
    if (!schoolId) {
      console.warn('No schoolId provided for subjects');
      return [];
    }

    // Check cache
    if (cachedSubjects &&
        cacheSchoolId === schoolId &&
        cacheTimestamp &&
        Date.now() - cacheTimestamp < CACHE_DURATION) {
      return cachedSubjects;
    }

    try {
      const collectionRef = this.getCollectionRef(schoolId);
      const q = query(collectionRef, orderBy('sortOrder', 'desc'));
      const snapshot = await getDocs(q);

      const subjects = [];
      snapshot.forEach((doc) => {
        subjects.push({ id: doc.id, ...doc.data() });
      });

      // Update cache
      cachedSubjects = subjects;
      cacheSchoolId = schoolId;
      cacheTimestamp = Date.now();

      return subjects;
    } catch (error) {
      console.error('Error fetching subjects:', error);
      return [];
    }
  }

  /**
   * Get active subjects only
   */
  async getActive(schoolId) {
    const all = await this.getAll(schoolId);
    return all.filter(s => s.active !== false);
  }

  /**
   * Get a single subject by ID
   */
  async getById(schoolId, subjectId) {
    if (!schoolId || !subjectId) {
      throw new Error('School ID and Subject ID are required');
    }

    try {
      const docRef = doc(this.getCollectionRef(schoolId), subjectId);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        return null;
      }

      return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
      console.error('Error fetching subject:', error);
      return null;
    }
  }

  /**
   * Subscribe to real-time updates
   */
  subscribe(schoolId, callback) {
    if (!schoolId) {
      console.warn('No schoolId provided for subjects subscription');
      callback([]);
      return () => {};
    }

    // Unsubscribe from previous listener if exists
    this.unsubscribe(schoolId);

    const collectionRef = this.getCollectionRef(schoolId);
    const q = query(collectionRef, orderBy('sortOrder', 'desc'));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const subjects = [];
        snapshot.forEach((doc) => {
          subjects.push({ id: doc.id, ...doc.data() });
        });

        // Update cache
        cachedSubjects = subjects;
        cacheSchoolId = schoolId;
        cacheTimestamp = Date.now();

        callback(subjects);
      },
      (error) => {
        console.error('Error in subjects subscription:', error);
        callback([]);
      }
    );

    this.listeners.set(schoolId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(schoolId) {
    const unsubscribe = this.listeners.get(schoolId);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(schoolId);
    }
  }

  /**
   * Create or update a subject
   */
  async save(schoolId, subjectData) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    // Validate
    const errors = this.validateSubject(subjectData);
    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }

    const { id, ...data } = subjectData;
    const subjectId = id || this.generateId(data.name);

    // Ensure defaults
    if (data.active === undefined) data.active = true;
    if (data.teacherIds === undefined) data.teacherIds = [];
    if (data.assessmentColumns !== undefined) {
      data.assessmentColumns = this.normaliseAssessmentColumns(data.assessmentColumns);
    }

    // Timestamps
    if (!id) {
      data.createdAt = serverTimestamp();
    }
    data.updatedAt = serverTimestamp();

    const docRef = doc(this.getCollectionRef(schoolId), subjectId);
    await setDoc(docRef, data, { merge: true });

    this.clearCache();

    return { id: subjectId, ...data };
  }

  /**
   * Delete a subject
   */
  async delete(schoolId, subjectId) {
    if (!schoolId || !subjectId) {
      throw new Error('School ID and Subject ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), subjectId);
    await deleteDoc(docRef);

    this.clearCache();
  }

  /**
   * Soft-delete (deactivate) a subject
   */
  async deactivate(schoolId, subjectId) {
    if (!schoolId || !subjectId) {
      throw new Error('School ID and Subject ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), subjectId);
    await updateDoc(docRef, {
      active: false,
      updatedAt: serverTimestamp()
    });

    this.clearCache();
  }

  /**
   * Reactivate a subject
   */
  async activate(schoolId, subjectId) {
    if (!schoolId || !subjectId) {
      throw new Error('School ID and Subject ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), subjectId);
    await updateDoc(docRef, {
      active: true,
      updatedAt: serverTimestamp()
    });

    this.clearCache();
  }

  /**
   * Add a teacher to a subject
   */
  async addTeacher(schoolId, subjectId, teacherId) {
    if (!schoolId || !subjectId || !teacherId) {
      throw new Error('School ID, Subject ID, and Teacher ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), subjectId);
    await updateDoc(docRef, {
      teacherIds: arrayUnion(teacherId),
      updatedAt: serverTimestamp()
    });

    this.clearCache();
  }

  /**
   * Remove a teacher from a subject
   */
  async removeTeacher(schoolId, subjectId, teacherId) {
    if (!schoolId || !subjectId || !teacherId) {
      throw new Error('School ID, Subject ID, and Teacher ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), subjectId);
    await updateDoc(docRef, {
      teacherIds: arrayRemove(teacherId),
      updatedAt: serverTimestamp()
    });

    this.clearCache();
  }

  /**
   * Get subjects for a specific teacher (client-side filter)
   */
  async getSubjectsForTeacher(schoolId, teacherId) {
    const all = await this.getAll(schoolId);
    return all.filter(s => s.teacherIds?.includes(teacherId) && s.active !== false);
  }

  /**
   * Validate subject data
   */
  validateSubject(subject) {
    const errors = [];

    if (!subject.name?.trim()) {
      errors.push('Subject name is required');
    }

    if (!subject.code?.trim()) {
      errors.push('Subject code is required');
    }

    if (subject.code && subject.code.length > 10) {
      errors.push('Subject code must be 10 characters or less');
    }

    if (subject.color && !this.isValidColor(subject.color)) {
      errors.push('Invalid color format (use #RRGGBB)');
    }

    // Assessment columns (term gradebook — knowledge/wiki/features/marks-gradebook.md).
    // Optional; a subject without them simply shows no gradebook columns yet.
    if (subject.assessmentColumns != null) {
      if (!Array.isArray(subject.assessmentColumns)) {
        errors.push('Assessment columns must be a list');
      } else {
        subject.assessmentColumns.forEach((col, i) => {
          if (!String(col?.label ?? '').trim()) {
            errors.push(`Assessment column ${i + 1}: a name is required`);
          }
          const max = Number(col?.maxScore);
          if (Number.isNaN(max) || max <= 0) {
            errors.push(`Assessment column ${i + 1}: "out of" must be greater than 0`);
          }
          const weight = col?.weight == null ? 1 : Number(col.weight);
          if (Number.isNaN(weight) || weight < 0) {
            errors.push(`Assessment column ${i + 1}: weight must be 0 or more`);
          }
        });
      }
    }

    return errors;
  }

  /**
   * Normalise assessment columns before write: coerce numbers, default weight to
   * 1, and assign a stable key (matched to a mark event's `assessmentName`).
   */
  normaliseAssessmentColumns(columns) {
    return (Array.isArray(columns) ? columns : []).map((col, i) => {
      const label = String(col?.label ?? '').trim();
      return {
        key: col?.key || this.generateColumnKey(label, i),
        label,
        maxScore: Number(col?.maxScore) || 100,
        weight: col?.weight == null ? 1 : Number(col.weight),
      };
    });
  }

  generateColumnKey(label, index = 0) {
    const slug = String(label || '')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .substring(0, 30);
    return slug ? `${slug}-${index}` : `col-${index}`;
  }

  /**
   * Helper: Generate ID from name
   */
  generateId(name) {
    return name.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .substring(0, 40);
  }

  /**
   * Helper: Validate color format
   */
  isValidColor(color) {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  }

  /**
   * Get default color palette
   */
  getDefaultColors() {
    return DEFAULT_COLORS;
  }
}

export const subjectsService = new SubjectsService();
export default subjectsService;
